import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Divider,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Tab,
  Tabs,
  Collapse,
  Stack,
  InputAdornment,
  TablePagination
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Description as DescriptionIcon,
  AddCircleOutline as AddCircleOutlineIcon,
  Save as SaveIcon,
  Code as CodeIcon,
  DataObject as DataObjectIcon,
  ArrowRight as ArrowRightIcon,
  Check as CheckIcon,
  Download as DownloadIcon,
  ContentCopy as ContentCopyIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowRight as KeyboardArrowRightIcon,
  Close as CloseIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
} from '@mui/icons-material';
import codexTypeService, { subscribeToCodexTypeEvents } from '../services/codexTypeService';
import processService from '../services/processService';
import groupService from '../services/groupService';
import RoleService from '../services/RoleService';
import useCan from '../hooks/useCan';

// Attribute data types
const ATTRIBUTE_DATA_TYPES = [
  { value: 'STRING', label: 'Text' },
  { value: 'NUMBER', label: 'Number' },
  { value: 'DATE', label: 'Date' },
  { value: 'BOOLEAN', label: 'Boolean' },
  { value: 'SELECT', label: 'Select' },
  { value: 'JSON', label: 'JSON Object' },
  { value: 'REGISTRY_LOOKUP', label: 'Registry Lookup' }
];


// Create a reusable JSON tree node component
const JsonTreeNode = ({ name, value, level = 0, expandAll = true }) => {
  const [expanded, setExpanded] = useState(expandAll);
  const isObject = value !== null && typeof value === 'object';
  const isArray = Array.isArray(value);
  
  const handleToggle = () => {
    setExpanded(!expanded);
  };
  
  const getValueDisplay = () => {
    if (value === null) return <Typography color="text.secondary" component="span">null</Typography>;
    if (typeof value === 'boolean') return <Typography color="error.main" component="span">{value.toString()}</Typography>;
    if (typeof value === 'number') return <Typography color="success.main" component="span">{value}</Typography>;
    if (typeof value === 'string') return <Typography color="primary.main" component="span">"{value}"</Typography>;
    
    // For objects and arrays, show a summary
    if (isArray) return <Typography color="text.secondary" component="span">Array[{value.length}]</Typography>;
    return <Typography color="text.secondary" component="span">Object{'{}'}</Typography>;
  };
  
  return (
    <Box sx={{ ml: level * 2 }}>
      <Box 
        sx={{ 
          display: 'flex', 
          alignItems: 'flex-start', 
          py: 0.5,
          cursor: isObject ? 'pointer' : 'default',
          "&:hover": { bgcolor: isObject ? 'action.hover' : 'transparent' },
          borderRadius: 1,
          transition: 'background-color 0.2s'
        }}
        onClick={isObject ? handleToggle : undefined}
      >
        {isObject && (
          <IconButton size="small" sx={{ p: 0, mr: 0.5 }}>
            {expanded ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
          </IconButton>
        )}
        
        {name !== undefined && (
          <Typography 
            variant="body2" 
            component="span" 
            fontWeight="bold" 
            sx={{ mr: 0.5 }}
          >
            {isArray ? `${name}:` : `"${name}":`}
          </Typography>
        )}
        
        {isObject ? (
          <Box component="span">
            {getValueDisplay()}
          </Box>
        ) : (
          getValueDisplay()
        )}
      </Box>
      
      {isObject && expanded && (
        <Box sx={{ mt: 0.5, pl: 1, borderLeft: '1px dashed rgba(0, 0, 0, 0.12)' }}>
          {isArray ? (
            value.map((item, index) => (
              <JsonTreeNode 
                key={index} 
                name={index}
                value={item} 
                level={level + 1}
                expandAll={expandAll}
              />
            ))
          ) : (
            Object.entries(value).map(([key, val]) => (
              <JsonTreeNode 
                key={key} 
                name={key} 
                value={val} 
                level={level + 1}
                expandAll={expandAll}
              />
            ))
          )}
        </Box>
      )}
    </Box>
  );
};

// Create a download function
const downloadJson = (data, filename) => {
  const json = JSON.stringify(data, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const href = URL.createObjectURL(blob);
  
  // Create a temporary link element
  const link = document.createElement('a');
  link.href = href;
  link.download = filename || 'schema.json';
  document.body.appendChild(link);
  link.click();
  
  // Clean up
  document.body.removeChild(link);
  URL.revokeObjectURL(href);
};

// Graphical JSON Viewer component with download option
const GraphicalJsonViewer = ({ data, filename = 'schema.json' }) => {
  const [viewMode, setViewMode] = useState('tree'); // 'tree' or 'raw'
  const [expandAll, setExpandAll] = useState(true); // Always start expanded
  
  const copyToClipboard = () => {
    const json = JSON.stringify(data, null, 2);
    navigator.clipboard.writeText(json)
      .then(() => {
        // Could add a snackbar notification here
        console.log('JSON copied to clipboard');
      })
      .catch(err => {
        console.error('Failed to copy JSON', err);
      });
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, alignItems: 'center' }}>
        <Box>
          <Button
            size="small"
            startIcon={viewMode === 'tree' ? <VisibilityOffIcon /> : <VisibilityIcon />}
            onClick={() => setViewMode(viewMode === 'tree' ? 'raw' : 'tree')}
          >
            {viewMode === 'tree' ? 'Raw View' : 'Tree View'}
          </Button>
        </Box>
        
        <Box>
          <Button
            size="small"
            startIcon={<ContentCopyIcon />}
            onClick={copyToClipboard}
            sx={{ mr: 1 }}
          >
            Copy
          </Button>
          
          <Button
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() => downloadJson(data, filename)}
            color="primary"
            variant="contained"
          >
            Download JSON
          </Button>
        </Box>
      </Box>
      
      <Paper 
        variant="outlined"
        sx={{ 
          p: 2, 
          maxHeight: 400, 
          overflow: 'auto',
          bgcolor: viewMode === 'raw' ? 'grey.50' : 'background.paper',
          fontFamily: viewMode === 'raw' ? 'monospace' : 'inherit'
        }}
      >
        {viewMode === 'tree' ? (
          <JsonTreeNode value={data} expandAll={expandAll} />
        ) : (
          <pre style={{ margin: 0, fontSize: '0.875rem' }}>
            {JSON.stringify(data, null, 2)}
          </pre>
        )}
      </Paper>
    </Box>
  );
};

// Specialized component for displaying JSON Schema structure in a more table-like format
const JsonSchemaStructureViewer = ({ schema }) => {
  // Check if schema is valid
  if (!schema || typeof schema !== 'object') {
    return <Typography color="text.secondary">Invalid schema format</Typography>;
  }
  
  // Display a friendly message for empty schemas
  if (Object.keys(schema).length === 0) {
    return <Typography color="text.secondary">Schema is empty</Typography>;
  }
  
  // Get property type display
  const getPropertyTypeInfo = (propSchema) => {
    // Handle cases where type might be an array or not defined
    let type = propSchema.type || 'any';
    
    // If type is an array, take the first non-null type
    if (Array.isArray(type)) {
      type = type.filter(t => t !== 'null')[0] || type[0] || 'any';
    }
    
    const types = {
      'string': { color: 'primary', icon: null, label: 'String' },
      'number': { color: 'success', icon: null, label: 'Number' },
      'integer': { color: 'success', icon: null, label: 'Integer' },
      'boolean': { color: 'warning', icon: null, label: 'Boolean' },
      'object': { color: 'secondary', icon: <DataObjectIcon fontSize="small" />, label: 'Object' },
      'array': { color: 'info', icon: null, label: 'Array' },
      'null': { color: 'default', icon: null, label: 'Null' },
      'any': { color: 'default', icon: null, label: 'Any' }
    };
    
    return types[type] || { color: 'default', icon: null, label: type || 'Any' };
  };
  
  // Helper to get constraints text for a property
  const getConstraints = (propSchema) => {
    const constraints = [];
    
    if (propSchema.minimum !== undefined) constraints.push(`min: ${propSchema.minimum}`);
    if (propSchema.maximum !== undefined) constraints.push(`max: ${propSchema.maximum}`);
    if (propSchema.minLength !== undefined) constraints.push(`minLength: ${propSchema.minLength}`);
    if (propSchema.maxLength !== undefined) constraints.push(`maxLength: ${propSchema.maxLength}`);
    if (propSchema.pattern) constraints.push(`pattern: ${propSchema.pattern}`);
    if (propSchema.enum) constraints.push(`enum: [${propSchema.enum.join(', ')}]`);
    if (propSchema.format) constraints.push(`format: ${propSchema.format}`);
    
    return constraints.join(', ');
  };
  
  return (
    <Box>
      {/* Display schema metadata if available */}
      {(schema.title || schema.description || schema.type || schema.$schema) && (
        <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
          <Grid container spacing={2}>
            {schema.title && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Title</Typography>
                <Typography variant="body1" fontWeight="medium">{schema.title}</Typography>
              </Grid>
            )}
            
            {schema.description && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                <Typography variant="body2">{schema.description}</Typography>
              </Grid>
            )}
            
            {schema.type && (
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                <Chip 
                  label={schema.type}
                  color={getPropertyTypeInfo({type: schema.type}).color}
                  size="small"
                  sx={{ mt: 0.5 }}
                />
              </Grid>
            )}
            
            {schema.$schema && (
              <Grid item xs={6}>
                <Typography variant="subtitle2" color="text.secondary">Schema Version</Typography>
                <Typography variant="body2">{schema.$schema}</Typography>
              </Grid>
            )}
          </Grid>
        </Paper>
      )}
      
      {/* Display properties section if available */}
      {schema.properties && Object.keys(schema.properties).length > 0 && (
        <>
          <Typography variant="h6" gutterBottom>Properties</Typography>
          
          {/* Main properties table */}
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                  <TableCell>Property</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Required</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Constraints</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(schema.properties).map(([propName, propSchema]) => {
                  const typeInfo = getPropertyTypeInfo(propSchema);
                  const required = schema.required && schema.required.includes(propName);
                  const constraints = getConstraints(propSchema);
                  
                  return (
                    <TableRow key={propName} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">{propName}</Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          icon={typeInfo.icon}
                          label={typeInfo.label}
                          color={typeInfo.color}
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {required ? 
                          <Chip size="small" label="SI" color="error" /> : 
                          <Chip size="small" label="NO" color="success" />
                        }
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{propSchema.description || '-'}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">{constraints || '-'}</Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
      
      {/* Display items info if this is an array schema */}
      {schema.type === 'array' && schema.items && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="h6" gutterBottom>Array Items</Typography>
          <Paper sx={{ p: 2 }}>
            <Typography variant="subtitle2" gutterBottom>Item Schema:</Typography>
            <JsonSchemaStructureViewer schema={schema.items} />
          </Paper>
        </Box>
      )}
      
      {/* Show message when no properties are defined */}
      {(!schema.properties || Object.keys(schema.properties).length === 0) && 
       schema.type !== 'array' && (
        <Alert severity="info" sx={{ mt: 2 }}>
          No properties defined in this schema.
        </Alert>
      )}
    </Box>
  );
};

// Function to format time difference
const getTimeAgo = (date) => {
  const now = new Date();
  const diff = now - new Date(date);
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const months = Math.floor(days / 30);
  const years = Math.floor(months / 12);

  if (years > 0) {
    return `più di ${years} ${years === 1 ? 'anno' : 'anni'} fa`;
  }
  if (months > 0) {
    return `più di ${months} ${months === 1 ? 'mese' : 'mesi'} fa`;
  }
  if (days > 14) {
    return 'più di 2 settimane fa';
  }
  if (days > 7) {
    return 'più di una settimana fa';
  }
  if (days > 0) {
    return `${days} ${days === 1 ? 'giorno' : 'giorni'} fa`;
  }
  const hours = Math.floor(diff / (1000 * 60 * 60));
  if (hours > 0) {
    return `${hours} ${hours === 1 ? 'ora' : 'ore'} fa`;
  }
  const minutes = Math.floor(diff / (1000 * 60));
  if (minutes > 0) {
    return `${minutes} ${minutes === 1 ? 'minuto' : 'minuti'} fa`;
  }
  return 'poco fa';
};

const CodexTypes = () => {
  const can = useCan();
  const navigate = useNavigate();
  // L'elenco si apre con codex_types.read; creare ed eliminare chiedono il proprio permesso,
  // altrimenti il pulsante porta a un 403. «Modifica» resta: il dettaglio si apre in lettura.
  const puoCreare = can('codex_types.create');
  const puoEliminare = can('codex_types.delete');

  // State for data
  const [codexTypes, setCodexTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  // State for filters and pagination
  const [searchQuery, setSearchQuery] = useState(() => {
    return sessionStorage.getItem('codexTypesSearchQuery') || '';
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // Fase F4: in quali gruppi sta ciascun tipo, e quali non stanno in nessuno. E' la lettura
  // che F1 aveva gia' preparato (findByCodexTypeId, idx_group_codex_type_type) e che fino a
  // qui non aveva una vista.
  const [appartenenza, setAppartenenza] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [codexTypeToDelete, setCodexTypeToDelete] = useState(null);

  // Load data on initialization
  useEffect(() => {
    fetchCodexTypes();
    
    // Subscribe to events
    const unsubscribeCodex = subscribeToCodexTypeEvents((event) => {
      fetchCodexTypes();
    });
    
    // Cleanup when component unmounts
    return () => {
      unsubscribeCodex();
    };
  }, []);

  useEffect(() => {
    sessionStorage.setItem('codexTypesSearchQuery', searchQuery);
  }, [searchQuery]);  

  // Sotto groups.read, e non chiesta a chi non ce l'ha: la pagina dei tipi resta leggibile
  // senza — senza questa sezione. Un fallimento e' silenzio e non un banner rosso: la
  // diagnosi e' un di piu' su una pagina che deve continuare a funzionare.
  useEffect(() => {
    // RoleService e non `can`: e' un controllo una tantum dentro un effetto, e con `can` fra le
    // dipendenze l'effetto ripartirebbe a ogni refresh() dei permessi.
    if (!RoleService.can('groups.read')) return;
    let vivo = true;
    groupService
      .appartenenzaTipi()
      .then((righe) => {
        if (vivo) setAppartenenza(righe || []);
      })
      .catch(() => {
        if (vivo) setAppartenenza(null);
      });
    return () => {
      vivo = false;
    };
  }, []);

  const gruppiDelTipo = React.useMemo(() => {
    const mappa = new Map();
    (appartenenza || []).forEach((riga) => mappa.set(riga.codexTypeId, riga));
    return mappa;
  }, [appartenenza]);

  // Nessun banner in cima: la colonna «Gruppi» dice lo stesso fatto riga per riga, con la
  // pillola «Nessun gruppo», e lo dice accanto al tipo che riguarda invece che in un
  // riquadro d'allarme che si legge prima ancora dell'elenco.

  const fetchCodexTypes = async () => {
    try {
      setLoading(true);
      const data = await codexTypeService.getAll();
      setCodexTypes(data);
    } catch (err) {
      console.error('Error fetching codex types:', err);
      setError('Failed to load codex types. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Event handlers
  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCreateCodexType = () => {
    navigate('/new-codex-type');
  };

  const handleEditCodexType = (codexType) => {
    navigate(`/codex-types/${codexType.id}`);
  };

  const handleDeleteCodexType = (codexType) => {
    setCodexTypeToDelete(codexType);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await codexTypeService.delete(codexTypeToDelete.id);
      setCodexTypes(codexTypes.filter(ct => ct.id !== codexTypeToDelete.id));
      setDeleteDialogOpen(false);
      setCodexTypeToDelete(null);
    } catch (error) {
      console.error('Error deleting codex type:', error);
      setError('Failed to delete codex type');
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCodexTypeToDelete(null);
  };

  // Filter data based on search query
  const filteredCodexTypes = React.useMemo(() => {
    return codexTypes.filter(codexType => {
      const matchesSearch = !searchQuery || 
        (codexType.code && codexType.code.toLowerCase().includes(searchQuery.toLowerCase())) ||
        (codexType.description && codexType.description.toLowerCase().includes(searchQuery.toLowerCase()));
      
      return matchesSearch;
    });
  }, [codexTypes, searchQuery]);

  if (loading && !codexTypes.length) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Tipi di Codex</Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                placeholder="Cerca per codice o descrizione..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchQuery('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                {puoCreare && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleCreateCodexType}
                  >
                    Aggiungi
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>
        </Box>
        
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
                <TableCell>Codice</TableCell>
                <TableCell>Descrizione</TableCell>
                <TableCell>Processi</TableCell>
                {appartenenza && <TableCell>Gruppi</TableCell>}
                <TableCell>Ultimo Aggiornamento</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredCodexTypes.length > 0 ? (
                filteredCodexTypes
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((codexType) => (
                    <TableRow key={codexType.id} hover>
                      <TableCell>
                        <Chip 
                          label={codexType.code || '-'} 
                          size="small" 
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{codexType.description || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={`${codexType.codexTypeProcesses?.length || 0} processi`}
                          size="small"
                          color="secondary"
                          variant="outlined"
                        />
                      </TableCell>
                      {appartenenza && (
                        <TableCell>
                          {(() => {
                            const riga = gruppiDelTipo.get(codexType.id);
                            const gruppi = riga ? riga.groups : [];
                            if (!gruppi || gruppi.length === 0) {
                              // «Nessun gruppo» e non una cella vuota: un trattino si legge
                              // come «non applicabile», e qui e' invece il guasto.
                              return (
                                <Chip
                                  label="Nessun gruppo"
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                              );
                            }
                            return (
                              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                                {gruppi.map((gruppo) => (
                                  <Chip
                                    key={gruppo.id}
                                    label={gruppo.label || gruppo.code}
                                    size="small"
                                    variant="outlined"
                                  />
                                ))}
                              </Stack>
                            );
                          })()}
                        </TableCell>
                      )}
                      <TableCell>
                        {codexType.updatedAt ? getTimeAgo(codexType.updatedAt) : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end">
                          <IconButton 
                            onClick={() => handleEditCodexType(codexType)}
                            size="small"
                            title="Modifica tipo codex"
                          >
                            <EditIcon />
                          </IconButton>
                          {puoEliminare && (
                            <IconButton
                              onClick={() => handleDeleteCodexType(codexType)}
                              size="small"
                              color="error"
                              title="Elimina tipo codex"
                            >
                              <DeleteIcon />
                            </IconButton>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
              ) : (
                <TableRow>
                  <TableCell colSpan={appartenenza ? 6 : 5} align="center">
                    Nessun tipo di codex trovato
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredCodexTypes.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <Typography>
            Sei sicuro di voler eliminare il tipo di codex "{codexTypeToDelete?.code}"? 
            Questa azione non può essere annullata.
            <Box component="span" sx={{ fontWeight: 'bold', color: 'error.main', display: 'block', mt: 1 }}>
              Attenzione: Questa azione potrebbe influenzare i codex che utilizzano questo tipo.
            </Box>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Annulla</Button>
          <Button 
            color="error" 
            variant="contained" 
            onClick={handleConfirmDelete}
          >
            Elimina
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CodexTypes; 